import { useSelector } from 'react-redux';
import { FormInput } from '~/components/FormComponents/FormInput.js';
import { useGeneralInfoSoleTraderForm } from './hooks/useGeneralInfoSoleTraderForm.ts';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Grid } from '@mui/material';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.js';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.js';
import { useAddressQuery } from '~/queries/useAddressQuery.js';
import { AddressInfo } from '../../components/FormComponents/AddressInfo.js';
import { DocumentInfo } from '../../components/FormComponents/DocumentInfo.tsx';
import LicenseExistence from '../../components/FormComponents/LicenseExistence/LicenseExistence.tsx';
import { PhoneList } from '../../components/FormComponents/PhoneList.tsx';
import { EmailList } from '../../components/FormComponents/EmailList.tsx';
import { AdditionQuestionnaire } from './components/AdditionQuestionnaire/AdditionQuestionnaire.tsx';
import { TGeneralInfoSoleTrader } from './types.ts';
import { errorMessages } from '~/constants/errorMessages.js';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  setIdentificationError,
  updateFormData,
  updateFormDataBack,
} from '~/store/identification-form/slices.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { cloneDeep } from 'lodash';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { FormPhoneField } from '~/components/FormComponents/FormPhoneField.tsx';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { trimValues } from '~/modules/identification/utils.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { styles } from '~/modules/identification/styles.ts';
import { testDataGeneralInfoSoleTrader } from '~/modules/identification/forms/GeneralInfo/constants.ts';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';

interface IGeneralInfoSoleTrader {
  onNextStep: () => void;
}

export const GeneralInfoSoleTrader = ({ onNextStep }: IGeneralInfoSoleTrader) => {
  const dispatch = useAppDispatch();
  const form = useGeneralInfoSoleTraderForm();
  const { isDirty, isSubmitting } = form.formState;
  const { countryOptions } = useAddressQuery();
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);
  const { isTestEnvironmentEnabled } = useFeatureFlags();

  const handleFillTestData = () =>
    form.reset(testDataGeneralInfoSoleTrader, { keepDefaultValues: true });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TGeneralInfoSoleTrader) => {
      if (isDirty) {
        try {
          const contactNumbers = values.contactNumbers.map((phone) => {
            const phoneNumber = phone?.replace(/\D/g, '') ?? '';
            return phoneNumber.length < 4 ? null : phone;
          });

          const generalInfoValues = {
            ...values,
            contactNumbers: contactNumbers.every((phone) => phone === null) ? null : contactNumbers,
          };

          const data = {
            generalInfo: trimValues(generalInfoValues),
            applicationId: appId as number,
          };

          const dataManagementStructure = {
            managementStructure: { noClientRepresentatives: true },
            applicationId: appId as number,
          };

          await dispatch(updateForm({ data, reportId: IdentificationFormSteps.generalInfo }));
          await dispatch(
            updateForm({
              data: dataManagementStructure,
              reportId: IdentificationFormSteps.managementStructure,
            })
          );
          const formData = {
            field: IdentificationFormStepNames.generalInfo as keyof TIdentificationForm,
            data: cloneDeep(values),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
          dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        {isTestEnvironmentEnabled && (
          <Box sx={styles.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <Grid container spacing={3}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <FormGridItem>
            <FormInput
              name="fullName"
              label="ПІБ"
              placeholder="Введіть повне прізвище, ім'я, по батькові"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormDateInput
              name="birthDate"
              label="Дата народження"
              placeholder="Введіть дату народження"
              isCurrentMaxDate
            />
          </FormGridItem>
          <AddressInfo showAddressCode isPersonAddress isDisabledCountry />
          <FormGridItem>
            <FormAutocomplete
              name="address.citizenshipCountry"
              options={countryOptions}
              inputProps={{
                placeholder: 'Оберіть країну громадянства',
              }}
              label="Країна громадянства"
            />
          </FormGridItem>
          <FormGridItem>
            <FormAutocomplete
              name="address.residenceCountry"
              options={countryOptions}
              disabled
              inputProps={{
                placeholder: 'Оберіть країну резидентства',
              }}
              label="Країна резидентства"
            />
          </FormGridItem>
          <FormGridItem>
            <FormAutocomplete
              name="address.birthplaceCountry"
              options={countryOptions}
              inputProps={{
                placeholder: 'Оберіть країну народження',
              }}
              label="Країна народження"
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="birthPlace"
              label="Місце народження"
              placeholder="Введіть місце народження"
              fullWidth
              inputProps={{
                maxLength: 200,
                minLength: 1,
              }}
            />
          </FormGridItem>
          <DocumentInfo
            citizenshipCountryPath="address.citizenshipCountry"
            residenceCountryPath="address.residenceCountry"
          />
          <FormGridItem sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
            <FormPhoneField name={`financialPhone`} label="Фінансовий номер телефону" />
          </FormGridItem>
          <PhoneList />
          <EmailList />
          <LicenseExistence isLicensedFieldName="license" />
          <AdditionQuestionnaire />
          <Grid size={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Продовжити
            </Button>
          </Grid>
        </Grid>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
        {isSubmitting && <FullPageLoader />}
      </Box>
    </FormProvider>
  );
};
